import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import {
  Button,
  Dropdown,
  Form,
  Icon,
  Input,
  Label,
  Tab,
  Table,
  TextArea,
} from 'semantic-ui-react';
import { useNavigate, useParams } from 'react-router-dom';
import { useToasts } from 'common/toast';

import PageTitle, { Container } from 'common/layouts/AuthLayout/PageTitle';
import { ResourceModal } from '../ResourceModal';
import { ListItemsInput } from './ListItemsInput';
import { PhaseTab } from '../PhaseTab';
import cloneDeep from 'lodash/cloneDeep';
import { RecommendationTarget } from '../../../../common/constants/RecommendationTarget';
import { RecommendationType } from '../../../../common/constants/RecommendationType';
import { templatesApi } from '../api/recommendation-templates';
import LoadingSpinner from 'common/components/LoadingSpinner';
import { Resource } from '../../../content-sharing/types/Resource';
import { DataOption } from '../../../../common/types/Option';
import { RecommendationActivity } from '../types/RecommendationActivity';
import AssociationModal from '../AssociationModal';
import { getFactorTextByValue } from '../utils/PillarUtils';
import { isNullish } from '../../../../types/IsNullish';
import { PILLARS } from 'common/constants/pillars';

const newEntityState = {
  title: '',
  description: '',
  target: RecommendationTarget.DEFICIT,
  phases: [{ activities: [] }, { activities: [] }, { activities: [] }],
  associations: [],
  context: {
    whyRecommendation: '',
    focusAreas: '',
    recommendationQuestion: '',
    organizationQuestions: [],
    effortRequires: [],
    involvedAreas: '',
    desiredOutcome: '',
    timeframe: '',
    cost: '',
    policyChange: '',
    cultureChange: '',
    dataCollection: '',
    communityConnections: '',
    primaryStakeholder: '',
  },
};

const recommendationTargetList = [
  {
    key: 1,
    value: {
      label: 'Organizational Deficits',
      data: { target: RecommendationTarget.DEFICIT },
    },
  },
  {
    key: 2,
    value: {
      label: 'Organizational Strengths',
      data: { target: RecommendationTarget.STRENGTH },
    },
  },
];

const OrganizationalGoal = () => {
  const navigate = useNavigate();
  const { id: templateId } = useParams();
  const { addToast } = useToasts();

  const [triggerUpdate] = templatesApi.useApiAdminV1RecommendationTemplateUpdateMutation();

  const [triggerInsert] = templatesApi.useApiAdminV1RecommendationTemplateCreateMutation();

  const { data: existingProject, isLoading: projectIsLoading } =
    templatesApi.useApiAdminV1RecommendationTemplateRetrieveQuery(
      {
        id: Number(templateId),
      },
      { skip: !templateId }
    );

  const [orgGoal, setOrgGoal] = useState<
    | {
        title: string;
        description: string;
        phases: any[];
        associations: {
          id?: any;
          factor: any;
          priority: number;
          summaryText: string;
          moreInfo: string;
        }[];
        context: any;
        target: any;
      }
    | undefined
  >(!templateId ? newEntityState : undefined);

  const [modalOpen, setModalOpen] = useState(false);
  const [currentResources, setCurrentResources] = useState<Resource[]>([]);
  const [currentactivityIndex, setCurrentactivityIndex] = useState<number | undefined>();
  const [currentPhaseIndex, setCurrentPhaseIndex] = useState<number | undefined>();
  const [currentTaskIndex, setCurrentTaskIndex] = useState<number | undefined>();

  const [associationToEdit, setAssociationToEdit] = useState<any>(undefined);

  useEffect(() => {
    if (existingProject) {
      setOrgGoal({
        title: existingProject.name,
        ...cloneDeep(existingProject.template),
      });
    } else if (!templateId) {
      // Reset state when creating a new template
      setOrgGoal(cloneDeep(newEntityState));
      setCurrentResources([]);
      setCurrentactivityIndex(undefined);
      setCurrentPhaseIndex(undefined);
      setCurrentTaskIndex(undefined);
      setAssociationToEdit(undefined);
    }
  }, [existingProject, templateId]);

  const saveOrgGoal = () => {
    if (!orgGoal) {
      return;
    }
    const updateId = templateId || null;
    const templatePayload = {
      id: updateId || null,
      name: orgGoal.title,
      status: 9,
      template: orgGoal,
      type_of: RecommendationType.ORGANIZATIONAL_GOALS,
      target: orgGoal.target,
    };

    if (updateId) {
      triggerUpdate({
        id: templatePayload.id,
        recommendationTemplate: templatePayload,
      } as any);
    } else {
      triggerInsert({
        id: templatePayload.id,
        recommendationTemplate: templatePayload,
      } as any);
    }

    addToast('Organizational Goal Saved', {
      appearance: 'success',
      autoDismiss: true,
      autoDismissTimeout: 1500,
    });
    navigate('/settings/admin/recommendation-templates/org-goals');
  };

  const handleSaveGoalAssociation = ({
    associationId,
    newAssociationFactor,
    newAssociationPriority,
    associationSummary,
    associationMoreInfo,
  }: {
    associationId: string;
    newAssociationFactor: any;
    newAssociationPriority: any;
    associationSummary: string;
    associationMoreInfo: string;
  }) => {
    if (!orgGoal) return;

    const updatedAssociation = {
      id: associationId,
      factor: newAssociationFactor,
      priority: newAssociationPriority,
      summaryText: associationSummary,
      moreInfo: associationMoreInfo,
    };

    const existingIndex = orgGoal.associations.findIndex(
      association => association.id === associationId
    );

    let updatedAssociations;
    if (existingIndex !== -1) {
      updatedAssociations = orgGoal.associations.map(association =>
        association.id === associationId ? updatedAssociation : association
      );
    } else {
      updatedAssociations = [...orgGoal.associations, updatedAssociation];
    }

    setOrgGoal({
      ...orgGoal,
      associations: updatedAssociations,
    });

    setAssociationToEdit(undefined);
  };

  const removeAssociation = (index: number) => {
    if (!orgGoal) {
      return;
    }
    const updatedAssociations = orgGoal.associations.filter((_, i) => i !== index);
    setOrgGoal({ ...orgGoal, associations: updatedAssociations });
  };

  const addActivity = (phaseIndex: number) => {
    if (!orgGoal) {
      return;
    }
    let updatedPhases = [...orgGoal.phases];
    updatedPhases[phaseIndex].activities.push({
      activityTitle: '',
      tasks: [],
    });
    setOrgGoal({
      ...orgGoal,
      phases: updatedPhases,
    });
  };

  const removeActivity = (phaseIndex: number, activityIndex: number) => {
    if (!orgGoal) {
      return;
    }
    let updatedPhases = [...orgGoal.phases];
    updatedPhases[phaseIndex].activities = orgGoal.phases[phaseIndex].activities.filter(
      (_: RecommendationActivity, i: number) => i !== activityIndex
    );
    setOrgGoal({ ...orgGoal, phases: updatedPhases });
  };

  const addTask = (phaseIndex: number, activityIndex: number) => {
    if (!orgGoal) {
      return;
    }
    const updatedPhases = [...orgGoal.phases];
    updatedPhases[phaseIndex].activities[activityIndex].tasks.push({
      task: '',
      metric: '',
      resources: [],
    });
    setOrgGoal({ ...orgGoal, phases: updatedPhases });
  };

  const removeTask = (phaseIndex: number, activityIndex: number, taskIndex: number) => {
    if (!orgGoal) {
      return;
    }
    let updatedPhases = [...orgGoal.phases];
    updatedPhases[phaseIndex].activities[activityIndex].tasks = updatedPhases[
      phaseIndex
    ].activities[activityIndex].tasks.filter((_: any, i: number) => i !== taskIndex);
    setOrgGoal({ ...orgGoal, phases: updatedPhases });
  };

  const openResourceModal = (
    phaseIndex: number,
    activityIndex: number,
    taskIndex: number,
    resources: Resource[]
  ) => {
    setCurrentPhaseIndex(phaseIndex);
    setCurrentactivityIndex(activityIndex);
    setCurrentTaskIndex(taskIndex);
    setCurrentResources(resources);
    setModalOpen(true);
  };

  const addResource = (phaseIndex: number, newResource: any) => {
    if (!orgGoal || isNullish(currentactivityIndex) || isNullish(currentTaskIndex)) {
      return;
    }
    const updatedResources = [...currentResources, newResource];
    const updatedPhases = [...orgGoal.phases];
    updatedPhases[phaseIndex].activities[currentactivityIndex].tasks[currentTaskIndex]['content'] =
      updatedResources;
    setOrgGoal({ ...orgGoal, phases: updatedPhases });
    setCurrentResources(updatedResources);
  };

  const removeResource = (phaseIndex: number, resourceIndex: number) => {
    if (
      !orgGoal ||
      !currentResources ||
      currentResources.length === 0 ||
      isNullish(currentactivityIndex) ||
      isNullish(currentTaskIndex)
    ) {
      return;
    }
    const updatedResources = currentResources.filter((_, i) => i !== resourceIndex);
    const updatedPhases = [...orgGoal.phases];
    updatedPhases[phaseIndex].activities[currentactivityIndex].tasks[currentTaskIndex]['content'] =
      updatedResources;
    setOrgGoal({ ...orgGoal, phases: updatedPhases });
    setCurrentResources(updatedResources);
  };

  const updateResourceInfo = (
    phaseIndex: number,
    resourceIndex: number,
    field: string,
    value: string
  ) => {
    if (
      !orgGoal ||
      isNullish(resourceIndex) ||
      isNullish(currentactivityIndex) ||
      isNullish(currentTaskIndex)
    ) {
      return;
    }
    const updatedResources = [...currentResources];
    // @ts-ignore
    updatedResources[resourceIndex][field] = value;
    const updatedPhases = [...orgGoal.phases];
    updatedPhases[phaseIndex].activities[currentactivityIndex].tasks[currentTaskIndex]['content'] =
      updatedResources;
    setOrgGoal({ ...orgGoal, phases: updatedPhases });
    setCurrentResources(updatedResources);
  };

  const updateActivityTitle = (phaseIndex: number, activityIndex: number, value: string) => {
    if (!orgGoal) {
      return;
    }
    const updatedPhases = [...orgGoal.phases];
    updatedPhases[phaseIndex].activities[activityIndex].activityTitle = value;
    setOrgGoal({ ...orgGoal, phases: updatedPhases });
  };

  const updateBCorpRelevance = (phaseIndex: number, activityIndex: number, value: string) => {
    if (!orgGoal) {
      return;
    }
    const updatedPhases = [...orgGoal.phases];
    updatedPhases[phaseIndex].activities[activityIndex].bCorpRelevance = value;
    setOrgGoal({ ...orgGoal, phases: updatedPhases });
  };

  const updateTaskInfo = (
    phaseIndex: number,
    activityIndex: number,
    taskIndex: number,
    field: string,
    value: string
  ) => {
    if (!orgGoal) {
      return;
    }
    const updatedPhases = [...orgGoal.phases];
    updatedPhases[phaseIndex].activities[activityIndex].tasks[taskIndex][field] = value;
    setOrgGoal({ ...orgGoal, phases: updatedPhases });
  };

  function onResourceModalClosed() {
    setModalOpen(false);
  }

  if (projectIsLoading || !orgGoal) {
    return <LoadingSpinner />;
  }

  const panes = [
    // Phase 1 Tab
    {
      menuItem: 'Discover',
      render: () => (
        <Tab.Pane attached={false}>
          <PhaseTab
            phaseIndex={0}
            addActivity={addActivity}
            phaseData={orgGoal.phases[0]}
            updateActivityTitle={updateActivityTitle}
            updateBCorpRelevance={updateBCorpRelevance}
            updateTaskInfo={updateTaskInfo}
            addTask={addTask}
            removeTask={removeTask}
            removeActivity={removeActivity}
            openResourceModal={openResourceModal}
          />
        </Tab.Pane>
      ),
    },
    // Phase 2 Tab
    {
      menuItem: 'Design',
      render: () => (
        <Tab.Pane attached={false}>
          <PhaseTab
            phaseIndex={1}
            addActivity={addActivity}
            phaseData={orgGoal.phases[1]}
            updateActivityTitle={updateActivityTitle}
            updateBCorpRelevance={updateBCorpRelevance}
            updateTaskInfo={updateTaskInfo}
            addTask={addTask}
            removeTask={removeTask}
            removeActivity={removeActivity}
            openResourceModal={openResourceModal}
          />
        </Tab.Pane>
      ),
    },
    // Phase 3 Tab
    {
      menuItem: 'Deploy',
      render: () => (
        <Tab.Pane attached={false}>
          <PhaseTab
            phaseIndex={2}
            addActivity={addActivity}
            phaseData={orgGoal.phases[2]}
            updateActivityTitle={updateActivityTitle}
            updateBCorpRelevance={updateBCorpRelevance}
            updateTaskInfo={updateTaskInfo}
            addTask={addTask}
            removeTask={removeTask}
            removeActivity={removeActivity}
            openResourceModal={openResourceModal}
          />
        </Tab.Pane>
      ),
    },
    // Context Fields Tab
    {
      menuItem: 'Context Fields',
      render: () => (
        <Tab.Pane attached={false}>
          <Form>
            <Form.Field>
              <label>Why make this recommendation?</label>
              <TextArea
                value={orgGoal.context.whyRecommendation}
                onChange={e =>
                  setOrgGoal({
                    ...orgGoal,
                    context: {
                      ...orgGoal.context,
                      whyRecommendation: e.target.value,
                    },
                  })
                }
              />
            </Form.Field>
            <Form.Field>
              <label>Focus Areas</label>
              <TextArea
                value={orgGoal.context.focusAreas}
                onChange={e =>
                  setOrgGoal({
                    ...orgGoal,
                    context: {
                      ...orgGoal.context,
                      focusAreas: e.target.value,
                    },
                  })
                }
              />
            </Form.Field>
            <Form.Field>
              <label>This recommendation aims to answer the question</label>
              <TextArea
                value={orgGoal.context.recommendationQuestion}
                onChange={e =>
                  setOrgGoal({
                    ...orgGoal,
                    context: {
                      ...orgGoal.context,
                      recommendationQuestion: e.target.value,
                    },
                  })
                }
              />
            </Form.Field>
            <ListItemsInput
              label="Questions that we need to answer about the organization"
              items={orgGoal.context.organizationQuestions}
              setItems={newItems =>
                setOrgGoal({
                  ...orgGoal,
                  context: {
                    ...orgGoal.context,
                    organizationQuestions: newItems,
                  },
                })
              }
            />
            <ListItemsInput
              label="This effort will require us to..."
              items={orgGoal.context.effortRequires}
              setItems={newItems =>
                setOrgGoal({
                  ...orgGoal,
                  context: {
                    ...orgGoal.context,
                    effortRequires: newItems,
                  },
                })
              }
            />
            <Form.Field>
              <label>Areas of the organization that will need to be involved</label>
              <TextArea
                value={orgGoal.context.involvedAreas}
                onChange={e =>
                  setOrgGoal({
                    ...orgGoal,
                    context: {
                      ...orgGoal.context,
                      involvedAreas: e.target.value,
                    },
                  })
                }
              />
            </Form.Field>
            <Form.Field>
              <label>Desired Outcomes</label>
              <TextArea
                value={orgGoal.context.desiredOutcome}
                onChange={e =>
                  setOrgGoal({
                    ...orgGoal,
                    context: {
                      ...orgGoal.context,
                      desiredOutcome: e.target.value,
                    },
                  })
                }
              />
            </Form.Field>
            <Form.Group widths="equal">
              <Form.Field>
                <label>Timeframe</label>
                <Input
                  value={orgGoal.context.timeframe}
                  onChange={e =>
                    setOrgGoal({
                      ...orgGoal,
                      context: {
                        ...orgGoal.context,
                        timeframe: e.target.value,
                      },
                    })
                  }
                />
              </Form.Field>
              <Form.Field>
                <label>Cost</label>
                <Input
                  value={orgGoal.context.cost}
                  onChange={e =>
                    setOrgGoal({
                      ...orgGoal,
                      context: {
                        ...orgGoal.context,
                        cost: e.target.value,
                      },
                    })
                  }
                />
              </Form.Field>
            </Form.Group>
            <Form.Group widths="equal">
              <Form.Field>
                <label>Policy Changes</label>
                <Input
                  value={orgGoal.context.policyChange}
                  onChange={e =>
                    setOrgGoal({
                      ...orgGoal,
                      context: {
                        ...orgGoal.context,
                        policyChange: e.target.value,
                      },
                    })
                  }
                />
              </Form.Field>
              <Form.Field>
                <label>Cultural Changes</label>
                <Input
                  value={orgGoal.context.cultureChange}
                  onChange={e =>
                    setOrgGoal({
                      ...orgGoal,
                      context: {
                        ...orgGoal.context,
                        cultureChange: e.target.value,
                      },
                    })
                  }
                />
              </Form.Field>
            </Form.Group>
            <Form.Field>
              <label>Data Collection</label>
              <Input
                value={orgGoal.context.dataCollection}
                onChange={e =>
                  setOrgGoal({
                    ...orgGoal,
                    context: {
                      ...orgGoal.context,
                      dataCollection: e.target.value,
                    },
                  })
                }
              />
            </Form.Field>
            <Form.Field>
              <label>Community Connections</label>
              <Input
                value={orgGoal.context.communityConnections}
                onChange={e =>
                  setOrgGoal({
                    ...orgGoal,
                    context: {
                      ...orgGoal.context,
                      communityConnections: e.target.value,
                    },
                  })
                }
              />
            </Form.Field>
            <Form.Field>
              <label>Primary Stakeholder</label>
              <Input
                value={orgGoal.context.primaryStakeholder}
                onChange={e =>
                  setOrgGoal({
                    ...orgGoal,
                    context: {
                      ...orgGoal.context,
                      primaryStakeholder: e.target.value,
                    },
                  })
                }
              />
            </Form.Field>
          </Form>
        </Tab.Pane>
      ),
    },
    // Associations Tab (updated)
    {
      menuItem: 'Associations',
      render: () => (
        <Tab.Pane attached={false}>
          {orgGoal.associations.length > 0 && (
            <Table
              celled
              selectable
              style={{
                borderRadius: '5px',
                boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
              }}
            >
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell width={3}>Factor</Table.HeaderCell>
                  <Table.HeaderCell width={6}>Summary</Table.HeaderCell>
                  <Table.HeaderCell width={1} textAlign="center">
                    Priority
                  </Table.HeaderCell>
                  <Table.HeaderCell width={1} textAlign="center">
                    Actions
                  </Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {orgGoal.associations.map((association: any, index: number) => (
                  <Table.Row
                    key={index}
                    onClick={() => setAssociationToEdit(association)}
                    style={{ cursor: 'pointer' }}
                  >
                    <Table.Cell>{getFactorTextByValue(PILLARS, association.factor)}</Table.Cell>
                    <Table.Cell title={association.summaryText}>
                      {association.summaryText.length > 150
                        ? `${association.summaryText.substring(0, 150)}...`
                        : association.summaryText}
                    </Table.Cell>
                    <Table.Cell textAlign="center">
                      <Label color={association.priority === 'primary' ? 'blue' : 'grey'}>
                        {association.priority.charAt(0).toUpperCase() +
                          association.priority.slice(1)}
                      </Label>
                    </Table.Cell>
                    <Table.Cell textAlign="center">
                      <IconButton
                        title="Delete Association"
                        onClick={(e: React.MouseEvent) => {
                          e.stopPropagation(); // prevent row's onClick from firing
                          removeAssociation(index);
                        }}
                      >
                        <Icon name="trash alternate" color="grey" />
                      </IconButton>
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          )}
          <Button icon labelPosition="left" primary onClick={() => setAssociationToEdit({})}>
            <Icon name="plus" />
            Add Association
          </Button>

          <AssociationModal
            pillarList={PILLARS}
            association={associationToEdit}
            open={!!associationToEdit}
            onClose={() => setAssociationToEdit(undefined)}
            onSave={handleSaveGoalAssociation}
          />
        </Tab.Pane>
      ),
    },
  ];

  return (
    <Container>
      <PageTitle newPageTitle="Organizational Goal" />

      <StyledForm>
        <Form.Field>
          <label>Title</label>
          <Input
            value={orgGoal.title}
            onChange={e => setOrgGoal({ ...orgGoal, title: e.target.value })}
          />
        </Form.Field>
        <Form.Field>
          <label>Description</label>
          <TextArea
            value={orgGoal.description}
            onChange={e => setOrgGoal({ ...orgGoal, description: e.target.value })}
          />
        </Form.Field>
        <Form.Field>
          <label>Supports</label>
          <Dropdown
            fluid
            selection
            options={recommendationTargetList.map((option: DataOption) => ({
              key: option.key,
              text: option.value.label,
              value: option.value.data.target,
            }))}
            value={orgGoal.target}
            onChange={(e, { value }) => setOrgGoal({ ...orgGoal, target: value })}
          />
        </Form.Field>
      </StyledForm>

      <Tab menu={{ secondary: true, pointing: true }} panes={panes} />

      <ResourceModal
        phaseIndex={currentPhaseIndex}
        open={modalOpen}
        onClose={onResourceModalClosed}
        resources={currentResources}
        addResource={addResource}
        removeResource={removeResource}
        updateResourceInfo={updateResourceInfo}
      />

      <SaveContainer>
        <Button onClick={() => navigate('/settings/admin/recommendation-templates/org-goals')}>
          Cancel
        </Button>
        <Button primary onClick={saveOrgGoal} disabled={!orgGoal.title}>
          Save Goal
        </Button>
      </SaveContainer>
    </Container>
  );
};

export default OrganizationalGoal;

const StyledForm = styled(Form)`
  margin-bottom: 20px;
`;
const IconButton = styled.button`
  padding: 8px !important;
  background-color: transparent !important;
  box-shadow: none !important;
  color: #d9534f !important;
  border: none;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
`;

const SaveContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 20px;
`;
